import '@nlux/themes/nova.css';
import '../../styles/io-chat-custom.css';
import React, { useCallback, useEffect, useState, useMemo, useContext, useRef } from 'react';
import { AiChat, EventsConfig } from '@nlux/react';
import { ReactReduxContext } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useChatAdapter } from './useChatAdapter';
import PlacesService from '../../services/PlacesService';
import { LocationTypeEnum } from '../../modules/places/model';
import {
  processDroppedGeoJsonFile,
  retrieveCachedGeoJson,
  uploadGeoJsonAsPlace,
  checkPlaceExists,
} from '../../utils/geojsonUpload';
import { Auth0Context } from '../../utils/contexts'; // Add this import
import { MapAction } from './langchain/langgraph/types/adapterOptions';
import { center } from '@turf/turf';

class DuplicateError extends Error {
  placeName?: string;

  constructor(message: string, placeName?: string) {
    super(message);
    this.name = 'DuplicateError';
    this.placeName = placeName;
  }
}

interface IProps {
  selectedThread: string;
  baseUrl: string;
  userData: any;
  token: string;
  handleSaveThreadHref: (string) => void;
  handleClick?: (url: URL, updateThreadHref?: boolean) => void; // Add this prop
  handleMapActions?: (actions: MapAction[]) => void;
}

// Thread history cache - outside component to persist across re-renders
const threadHistoryCache = new Map<string, { history: any[]; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL

// LRU-style cache with max size limit
const MAX_CACHED_THREADS = 20;

function addToCache(threadId: string, history: any[]) {
  // Remove oldest entry if at capacity
  if (threadHistoryCache.size >= MAX_CACHED_THREADS) {
    const oldestKey = threadHistoryCache.keys().next().value;
    threadHistoryCache.delete(oldestKey);
  }
  threadHistoryCache.set(threadId, { history, timestamp: Date.now() });
}

const IOChat = (props: IProps) => {
  const { t, i18n } = useTranslation();

  const { selectedThread, baseUrl, userData, token, handleSaveThreadHref, handleMapActions } =
    props;
  const { store } = useContext(ReactReduxContext);

  // Add Auth0 context like in Header component
  const { selectedWorkspace, groups } = useContext(Auth0Context);

  // Print userData to console
  // console.log('userData:', userData);
  // console.log('store.getState().global.workspaces:', store.getState().global.workspaces);
  // console.log('Auth0 selectedWorkspace:', selectedWorkspace);
  // console.log('Auth0 groups:', groups);

  const [conversationHistory, setConversationHistory] = useState([]);
  const [isLoaded, setLoaded] = useState(false);

  // Log current location from router state
  useEffect(() => {
    const router = store.getState().router;
    const routerType = router?.type;
    const routerPayload = router?.payload;

    if (routerType === 'EARTH') {
      console.log('🗺️ Current location: /earth');
    } else if (routerType === 'LOCATION' && routerPayload) {
      const workspace = routerPayload.organization || routerPayload.slug?.split('/')?.[0];
      const place = routerPayload.slug;
      console.log(`🗺️ Current location: /location/${workspace}/${place}`);
    } else {
      console.log('🗺️ Current location:', router?.pathname || 'unknown');
    }
  }, [store.getState().router?.type, store.getState().router?.payload?.slug]);
  // Add drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  // Track if we should invalidate cache for current thread
  const shouldInvalidateCache = useRef(false);

  // Add a ref to track if initial load is complete
  const isInitialLoadComplete = useRef(false);

  // Function to invalidate cache for current thread (call after sending a message)
  const invalidateThreadCache = useCallback(() => {
    if (selectedThread) {
      threadHistoryCache.delete(selectedThread);
    }
  }, [selectedThread]);

  // Add function to handle GeoJSON import events
  const handleGeoJsonImport = useCallback(
    async (event) => {
      // console.log('=== IOChat: GeoJSON Import Event Received ===');
      // console.log('Event detail:', event.detail);

      const { cacheKey, filename, featureCount, size } = event.detail.data;

      try {
        // Retrieve cached GeoJSON data using utility
        const cachedResult = retrieveCachedGeoJson(cacheKey);

        if (cachedResult) {
          // console.log('=== Cached GeoJSON Data Retrieved ===');
          // console.log('Cache Key:', cacheKey);
          // console.log('Filename:', filename);
          // console.log('Feature Count:', featureCount);
          // console.log('File Size:', size, 'bytes');

          const { geoJson: geoJsonData, metadata } = cachedResult;
          // console.log('GeoJSON Type:', geoJsonData.type);
          // console.log('Full GeoJSON Structure:', geoJsonData);

          if (metadata) {
            // console.log('Metadata:', metadata);
          }

          // Log sample of features if it's a FeatureCollection
          if (geoJsonData.type === 'FeatureCollection' && geoJsonData.features?.length > 0) {
            // console.log('--- Feature Analysis ---');
            // console.log('Total Features:', geoJsonData.features.length);
            // console.log('First Feature:', geoJsonData.features[0]);

            // Analyze geometry types
            const geometryTypes = geoJsonData.features.reduce((acc, feature) => {
              const type = feature.geometry?.type;
              acc[type] = (acc[type] || 0) + 1;
              return acc;
            }, {});
            // console.log('Geometry Types:', geometryTypes);

            // Show sample properties
            const sampleProperties = geoJsonData.features[0]?.properties;
            // console.log('Sample Properties:', sampleProperties);
          }

          // console.log('=== End GeoJSON Analysis ===');

          // Upload the GeoJSON as a new place
          try {
            // console.log('=== Uploading GeoJSON to Backend ===');
            const uploadResult = await uploadGeoJsonAsPlace(geoJsonData, filename, userData);
            // console.log('Upload successful! New place created:', uploadResult);
          } catch (uploadError) {
            console.error('Failed to upload GeoJSON to backend:', uploadError);
          }
        } else {
          console.warn('No cached data found for key:', cacheKey);
        }
      } catch (error) {
        console.error('Error retrieving cached GeoJSON:', error);
      }
    },
    [userData]
  );

  // Set up event listener for GeoJSON imports
  useEffect(() => {
    window.addEventListener('geojson-import', handleGeoJsonImport);

    // Also expose a global method for direct access
    const windowAny = window as any;
    windowAny.IOChat = {
      receiveGeoJsonImport: (message) => {
        // console.log('=== IOChat: Direct GeoJSON Import ===');
        handleGeoJsonImport({ detail: message });
      },
      retrieveCachedGeoJson: retrieveCachedGeoJson,
    };

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('geojson-import', handleGeoJsonImport);
      // Clean up global reference
      if (windowAny.IOChat) {
        delete windowAny.IOChat;
      }
    };
  }, [handleGeoJsonImport]);

  useEffect(() => {
    let isMounted = true;

    // Reset loading state immediately when thread changes
    setLoaded(false);
    setConversationHistory([]);
    isInitialLoadComplete.current = false; // Reset on thread change

    async function getChatHistory() {
      if (!selectedThread) return null;

      // Check cache first
      const cached = threadHistoryCache.get(selectedThread);
      const now = Date.now();

      if (cached && now - cached.timestamp < CACHE_TTL_MS) {
        console.log('Using cached history for thread:', selectedThread);
        return { fromCache: true, data: cached.history };
      }

      // Fetch from server with error handling
      console.log('Fetching history from server for thread:', selectedThread);
      try {
        const response = await fetch(`${baseUrl}/threads/${selectedThread}/history`, {
          method: 'GET',
        });

        if (!response.ok) {
          console.error(
            `Failed to fetch thread history: ${response.status} ${response.statusText}`
          );
          return { fromCache: false, data: null };
        }

        const data = await response.json();

        // Log the size of the history and metadata
        if (Array.isArray(data) && data.length > 0) {
          const historyJson = JSON.stringify(data);
          const historySizeKB = (new Blob([historyJson]).size / 1024).toFixed(2);

          const messages = data[0]?.values?.messages;
          const messagesSizeKB = messages
            ? (new Blob([JSON.stringify(messages)]).size / 1024).toFixed(2)
            : '0.00';

          const metadata = data[0]?.metadata;
          const metadataSizeKB = metadata
            ? (new Blob([JSON.stringify(metadata)]).size / 1024).toFixed(2)
            : '0.00';

          // console.log(
          //   `DEBUG IOChat thread ${selectedThread} - history: ${historySizeKB} kB, messages: ${messagesSizeKB} kB, metadata: ${metadataSizeKB} kB`
          // );

          // Log size of each metadata key
          if (metadata) {
            const metadataKeys = Object.keys(metadata);
            const metadataSizes = metadataKeys.map((key) => {
              const valueJson = JSON.stringify(metadata[key]);
              const valueSizeKB = (new Blob([valueJson]).size / 1024).toFixed(2);
              return `${key}: ${valueSizeKB} kB`;
            });
            // console.log(`DEBUG IOChat metadata: ${metadataSizes.join(', ')}`);
          }

          // Log raw scores if present
          // const scores = data[0]?.values?.scores;
          // if (scores) {
          //   console.log(`  scores (${scores.length} items):`, JSON.stringify(scores));
          // }
        }

        return { fromCache: false, data };
      } catch (error) {
        console.error('Error fetching thread history:', error);
        return { fromCache: false, data: null };
      }
    }

    getChatHistory()
      .then((result) => {
        if (!isMounted) return;

        if (!result) {
          setLoaded(true);
          return;
        }

        const { fromCache, data } = result;

        if (data) {
          let processedHistory: any[] = [];

          if (fromCache) {
            // Data is already processed
            processedHistory = data;
          } else if (Array.isArray(data) && data.length > 0) {
            // Safely process raw server response with optional chaining
            try {
              const messages = data[0]?.values?.messages;
              if (Array.isArray(messages)) {
                processedHistory = messages.reduce(function (filtered, entry) {
                  if (['human', 'ai'].includes(entry.type) && entry.content) {
                    filtered.push({
                      message: entry.content,
                      role: entry.type === 'human' ? 'user' : 'assistant',
                    });
                  }
                  return filtered;
                }, []);
              }

              // Cache the processed history
              if (processedHistory.length > 0) {
                threadHistoryCache.set(selectedThread, {
                  history: processedHistory,
                  timestamp: Date.now(),
                });
              }
            } catch (parseError) {
              console.error('Error parsing thread history:', parseError);
            }
          }

          setConversationHistory(processedHistory);
        }

        setLoaded(true);

        // Mark initial load as complete after a brief delay to ensure AiChat has rendered
        setTimeout(() => {
          if (isMounted) {
            isInitialLoadComplete.current = true;
          }
        }, 100);
      })
      .catch((error) => {
        console.error('Unexpected error in getChatHistory:', error);
        if (isMounted) {
          setConversationHistory([]);
          setLoaded(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedThread, baseUrl]);

  // Invalidate cache when a new message is sent
  const inputPreProcessor = useCallback(
    (input, conversationHistory) => {
      // Invalidate cache since we're adding a new message
      invalidateThreadCache();

      // Check and log current location from router
      const router = store.getState().router;
      const routerType = router?.type;
      const routerPayload = router?.payload;
      let currentLocation = '/earth';

      if (routerType === 'EARTH') {
        currentLocation = '/earth';
        // console.log('🗺️ Current location:', currentLocation);
      } else if (routerType === 'LOCATION' && routerPayload) {
        const workspace = routerPayload.organization || routerPayload.slug?.split('/')?.[0];
        const place = routerPayload.slug;
        currentLocation = `/location/${workspace}/${place}`;
        // console.log('🗺️ Current location:', currentLocation);
      }
      console.log('🗺️ Current location:', currentLocation);

      // Use workspaces from Auth0 context instead of Redux store
      const currentWorkspace = Array.isArray(selectedWorkspace)
        ? selectedWorkspace[0]
        : selectedWorkspace;

      // Ensure we have a valid workspace, fallback to 'common' if not
      const validWorkspace = currentWorkspace || 'common';

      // Create workspaces array similar to Header component logic
      const workspacesArray = [validWorkspace];
      if (validWorkspace !== 'common') {
        workspacesArray.push('common');
      }

      // console.log('DEBUG IOChat config workspaces:', workspacesArray); // Enable this for debugging

      const viewport = store.getState().map.viewport;

      // Get bounds directly from react-map-gl map instance if available
      let bounds = null;
      const mapRef = (window as any).mapRef;
      if (mapRef && mapRef.getBounds) {
        const mapBounds = mapRef.getBounds();
        bounds = [
          mapBounds.getWest(),
          mapBounds.getSouth(),
          mapBounds.getEast(),
          mapBounds.getNorth(),
        ];
      } else {
        // Fallback to Redux store bounds if map not available
        bounds = store.getState().map.bounds || null;
      }

      function clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max);
      }

      bounds[0] = clamp(bounds[0], -180, 180);
      bounds[2] = clamp(bounds[2], -180, 180);
      bounds[1] = clamp(bounds[1], -90, 90);
      bounds[3] = clamp(bounds[3], -90, 90);

      let mapState = {
        location: currentLocation,
        center: viewport ? [viewport.longitude, viewport.latitude] : [0, 0],
        zoom: viewport ? Math.floor(viewport.zoom) : 2,
        bounds: bounds,
        layers: store.getState().layers.active,
      };
      console.log('IOChat: Prepared map state for message:', mapState);

      return {
        assistant_id: 'fe096781-5601-53d2-b2f6-0d3403f7e9ca', // WARNING hard coded value!
        input: {
          messages: [
            {
              role: 'human',
              content: input,
            },
          ],
        },
        config: {
          configurable: {
            // chathistory: conversationHistory,
            token: token,
            userdata: userData,
            workspaces: workspacesArray, // Use Auth0-derived workspaces
            language: i18n.language || store.getState().global.language || 'en',
            map_state: mapState,
          },
        },
      };
    },
    [
      token,
      userData,
      selectedThread,
      handleSaveThreadHref,
      i18n.language,
      store,
      selectedWorkspace,
      invalidateThreadCache,
    ]
  );

  // Make sure the adapter is recreated properly and not cached incorrectly
  const adapter = useChatAdapter({
    url: `${baseUrl}/threads/${selectedThread}/runs`,
    inputPreProcessor: inputPreProcessor,
    useInputSchema: false,
    dataTransferMode: 'stream',
    mapActionsProcessor: handleMapActions,
  });

  const messageReceivedCallback = useCallback((message) => {
    // console.log(message);
  }, []);

  // Add debugging for when messages are sent
  const handleMessageSend = useCallback(
    (message) => {
      // console.log('🚀 IOChat: User sending message:', message);
      // console.log('🔍 Current adapter:', adapter);
    },
    [adapter]
  );

  // Fix the drag and drop handlers by removing the circular dependency
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prevCounter) => {
      const newCounter = prevCounter + 1;
      setIsDragOver(newCounter > 0);
      return newCounter;
    });
  }, []); // Remove dragCounter dependency

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prevCounter) => {
      const newCounter = prevCounter - 1;
      setIsDragOver(newCounter > 0);
      return newCounter;
    });
  }, []); // Remove dragCounter dependency

  const processDroppedFile = useCallback(
    async (file: File) => {
      // Add authentication check
      if (!token || !userData?.organization) {
        console.error('Cannot upload: user not authenticated');
        return;
      }

      const handleSuccess = (uploadResult: any, metadata: any) => {
        // Create the automated message
        const placeName = uploadResult.name || metadata.filename.replace(/\.[^/.]+$/, '');
        const automaticMessage = `I've uploaded a GeoJSON file "${
          file.name
        }" as a new place called "${placeName}". The place contains ${
          metadata.featureCount
        } feature${metadata.featureCount !== 1 ? 's' : ''}. Show this place on the map`;

        // Add the automatic message to conversation history
        setConversationHistory((prev) => [
          ...prev,
          {
            message: automaticMessage,
            role: 'user',
          },
        ]);

        // Get chatbot response and add it to conversation
        if (adapter && adapter.streamText) {
          let accumulatedResponse = '';

          adapter
            .streamText(
              automaticMessage,
              {
                next: (chunk) => {
                  // Accumulate the streamed chunks
                  if (typeof chunk === 'string') {
                    accumulatedResponse += chunk;
                  }
                },
                error: (error) => {
                  console.error('Error in chatbot response:', error);
                },
                complete: () => {
                  console.log('Chatbot response to GeoJSON upload:', accumulatedResponse);

                  // Add the complete response to conversation history
                  if (accumulatedResponse) {
                    setConversationHistory((prev) => [
                      ...prev,
                      {
                        message: accumulatedResponse,
                        role: 'assistant',
                      },
                    ]);
                  }
                },
              },
              {
                conversationHistory: conversationHistory,
                aiChatProps: {},
              }
            )
            .catch((error) => {
              console.error('Error in chatbot response:', error);
            });
        }
      };

      const handleError = async (error: any) => {
        console.error('Processing failed:', error);
        console.error('Error name:', error?.name);
        console.error('Error message:', error?.message);
        console.error('Error response data:', error?.data);

        let userMessage;

        // Check if it's our custom duplicate error
        if (error instanceof DuplicateError) {
          console.log('Detected duplicate place upload');

          const placeName = error.placeName || file.name.replace(/\.[^/.]+$/, '');
          userMessage = `Search for place ${placeName} and show that place on the map`;
        } else {
          // Extract error message from various possible error formats
          let errorMessage = error?.message || error?.data?.message || error?.data?.error;

          // If error is an axios response object
          if (!errorMessage && error?.data) {
            if (typeof error.data === 'string') {
              errorMessage = error.data;
            } else if (error.data.errors && Array.isArray(error.data.errors)) {
              errorMessage = error.data.errors.map((e: any) => e.detail || e.title || e).join(', ');
            }
          }

          // Fallback to status code if no message found
          if (!errorMessage && error?.status) {
            errorMessage = `HTTP ${error.status} error`;
          }

          // Final fallback
          if (!errorMessage) {
            errorMessage = 'Unknown error occurred';
          }

          userMessage = `I attempted to upload your GeoJSON file "${file.name}" but encountered an error: ${errorMessage}. Can you help me troubleshoot this issue?`;
        }

        // Add error message to conversation history
        setConversationHistory((prev) => [
          ...prev,
          {
            message: userMessage,
            role: 'user',
          },
        ]);

        // Get chatbot response for error
        if (adapter && adapter.batchText) {
          adapter
            .batchText(userMessage, {
              conversationHistory: conversationHistory,
              aiChatProps: {},
            })
            .then((response) => {
              console.log('Chatbot response to upload error:', response);

              // Extract the final AI response from the conversation array
              if (response && Array.isArray(response)) {
                const lastAiMessage = response
                  .filter((msg) => msg.type === 'ai' || msg.role === 'assistant')
                  .pop();

                if (lastAiMessage && lastAiMessage.content) {
                  setConversationHistory((prev) => [
                    ...prev,
                    {
                      message: lastAiMessage.content,
                      role: 'assistant',
                    },
                  ]);
                }
              } else if (response && typeof response === 'string') {
                setConversationHistory((prev) => [
                  ...prev,
                  {
                    message: response,
                    role: 'assistant',
                  },
                ]);
              }
            })
            .catch((error) => {
              console.error('Error in chatbot response:', error);
            });
        }
      };

      try {
        await processDroppedGeoJsonFile(file, userData, handleSuccess, handleError);
      } catch (error) {
        // Error already handled by handleError callback
      }
    },
    [userData, adapter, conversationHistory, token] // Add token to dependencies
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      const files = Array.from(e.dataTransfer.files) as File[];
      const jsonFiles = files.filter(
        (file: File) =>
          file.type === 'application/json' ||
          file.name.toLowerCase().endsWith('.geojson') ||
          file.name.toLowerCase().endsWith('.json')
      );

      if (jsonFiles.length === 0) {
        alert('Please drop a JSON or GeoJSON file (.json or .geojson)');
        return;
      }

      // console.log(`Processing ${jsonFiles.length} JSON/GeoJSON files in IOChat`);
      jsonFiles.forEach(processDroppedFile);
    },
    [processDroppedFile]
  );

  if (!isLoaded) {
    return <div>Loading</div>;
  }

  const conversationStarters = {
    io: [
      {
        label: 'Show my project area',
        prompt: "Show my team's default AOI.",
      },
      {
        label: 'What data is available over my project area?',
        prompt: "Show a list of my team's AOIs and datasets.",
      },
      {
        label: '日本語を話せますか',
        prompt: 'Please always respond in both Japanese and English',
      },
    ],
  };

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(isDragOver && {
          backgroundColor: 'rgba(0, 123, 255, 0.05)',
          border: '2px dashed #007bff',
          borderRadius: '8px',
        }),
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="drag-overlay">
          <div className="drag-overlay-content">
            <div className="file-icon">📄</div>
            Drop JSON/GeoJSON file here
            <br />
            <small style={{ fontWeight: 'normal', fontSize: '14px' }}>
              Will be processed and made available to the AI assistant
            </small>
          </div>
        </div>
      )}

      <AiChat
        adapter={adapter}
        className="ioChat" // apply the class styling
        events={{
          messageReceived: messageReceivedCallback,
        }}
        messageOptions={{
          markdownLinkTarget: 'self',
          streamingAnimationSpeed: 5, // default is 10
          waitTimeBeforeStreamCompletion: 'never', // This prevents auto-completion
        }}
        personaOptions={{
          assistant: {
            name: '',
            avatar: '/static/bob_avatar.png',
            tagline: t(
              `Hi! I'm Bob, your new geospatial data librarian and imagery analyst. 
               I'm here to support you find and access relevant satellite imagery, 
               geospatial data, and analytics for your areas of interest. 
               I can assist with orchestration and production of new analytics and 
               tasking of new data collection. I'll also help investigate what's 
               driving observed changes using metrics, prior reporting, and the 
               latest news.`
            ),
          },
          user: {
            name: userData.name || 'User',
            avatar: userData.avatar || '/static/default_user_avatar.png',
          },
        }}
        conversationOptions={{
          showWelcomeMessage: true, // or false if you don't want to show it
          conversationStarters: conversationStarters[userData.organization] || [
            {
              label: 'Show my project area',
              prompt: "Show my team's default AOI.",
            },
            {
              label: 'What data is available over my project area?',
              prompt: "Show a list of my team's AOIs and datasets.",
            },
            {
              label: '日本語を話せますか',
              prompt: 'Please always respond in both Japanese and English',
            },
          ],
          layout: 'bubbles',
        }}
        initialConversation={conversationHistory.length > 0 ? conversationHistory : null}
        promptBoxOptions={{
          autoFocus: true,
          placeholder: 'Type your message here',
          // These may help prevent extension interference
          inputProps: {
            autoComplete: 'off',
            'data-lpignore': 'true', // LastPass
            'data-1p-ignore': 'true', // 1Password
            'data-form-type': 'other', // Generic hint
          },
        }}
        displayOptions={{
          height: '100%',
          width: '100%',
        }}
        key={selectedThread}
        onMessageSend={handleMessageSend} // Add this if supported
      />
    </div>
  );
};

// Optional: Export cache utilities for external use
export const clearThreadHistoryCache = () => threadHistoryCache.clear();
export const clearThreadFromCache = (threadId: string) => threadHistoryCache.delete(threadId);

export default IOChat;
